<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { type VariantProps, cva } from 'class-variance-authority'
import { Marked } from 'marked'
import sanitizeHtml from 'sanitize-html'
import { computed } from 'vue'

const markdown = new Marked({ breaks: true })

const propsCva = cva('ai-markdown', {
  variants: {
    variant: {
      default: '',
      dimmed: 'ai-markdown--dimmed'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

type MarkdownVariant = VariantProps<typeof propsCva>['variant']

const { text, variant } = defineProps<{
  text: string
  variant?: MarkdownVariant
}>()

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [...sanitizeHtml.defaults.allowedTags, 'del'],
  allowedClasses: { ol: ['prioritized'] }
}

const html = computed(() => sanitizeHtml(markdown.parse(text, { async: false }), SANITIZE_OPTIONS))
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div :class="propsCva({ variant })" v-html="html"></div>
</template>

<style scoped>
/* stylelint-disable selector-pseudo-class-no-unknown */
.ai-markdown {
  --ai-markdown-title-color: var(--font-color);

  font-size: var(--font-size-normal);
  line-height: var(--dimension-7);
  letter-spacing: 0;
  color: var(--font-color-strong);
  overflow-wrap: break-word;

  /* Overrides the theme's `* { letter-spacing }`, which inheritance cannot. */
  & :deep(*) {
    letter-spacing: 0;
  }

  & :deep(:is(p, ul, ol, pre, blockquote, table)) {
    margin: 0 0 var(--dimension-5);
  }

  & :deep(:first-child) {
    margin-top: 0;
  }

  & :deep(:last-child) {
    margin-bottom: 0;
  }

  & :deep(:is(h1, h2, h3, h4, h5, h6)) {
    margin: var(--dimension-8) 0 var(--dimension-4);
    padding: 0;
    font-size: var(--font-size-normal);
    line-height: var(--dimension-7);
  }

  & :deep(h1) {
    font-size: var(--font-size-xlarge);
  }

  & :deep(h2) {
    font-size: var(--font-size-large);
  }

  & :deep(:is(h1, h2, h3, h4, h5, h6, strong, b, th)) {
    font-weight: var(--font-weight-bold);
    color: var(--ai-markdown-title-color);
  }

  & :deep(:is(strong, b)) {
    font-weight: 600;
  }

  & :deep(:is(ul, ol)) {
    padding-left: var(--dimension-6);
  }

  & :deep(ul) {
    list-style-type: disc;
  }

  /* `prioritized` is set by the model in its HTML output, so it cannot follow BEM. */
  /* stylelint-disable checkmk/vue-bem-naming-convention */
  & :deep(ol.prioritized) {
    display: flex;
    flex-direction: column;
    gap: var(--dimension-5);
    padding: 0;
    list-style: none;
    counter-reset: prioritized;
  }

  & :deep(ol.prioritized > li) {
    position: relative;
    padding: calc(var(--dimension-4) + var(--dimension-2)) var(--dimension-5);
    padding-left: var(--dimension-11);
    border-radius: var(--border-radius);
    background-color: var(--ux-theme-3);
    counter-increment: prioritized;
  }

  & :deep(ol.prioritized > li::before) {
    content: counter(prioritized);
    position: absolute;
    top: var(--dimension-5);
    left: var(--dimension-5);
    width: var(--dimension-6);
    height: var(--dimension-6);
    border-radius: 50%;
    background-color: var(--ai-purple);
    color: var(--white);
    font-size: var(--font-size-normal);
    font-weight: var(--font-weight-bold);
    line-height: var(--dimension-6);
    text-align: center;
  }
  /* stylelint-enable checkmk/vue-bem-naming-convention */

  & :deep(:is(pre, code)) {
    font-family: monospace;
  }

  & :deep(:not(pre) > code) {
    padding: 0 var(--dimension-3);
    border-radius: var(--dimension-2);
    background-color: var(--code-background-color);
    overflow-wrap: anywhere;
  }

  & :deep(pre) {
    padding: var(--dimension-4) var(--dimension-5);
    overflow-x: auto;
    border-radius: var(--border-radius);
    background-color: var(--code-background-color);
    color: var(--ai-markdown-title-color);
  }

  & :deep(blockquote) {
    padding-left: var(--dimension-5);
    border-left: var(--dimension-2) solid var(--ux-theme-5);
  }

  /* Block display lets wide tables scroll inside the narrow panel. */
  & :deep(table) {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }

  & :deep(:is(th, td)) {
    padding: var(--dimension-3) var(--dimension-4);
    border: var(--border-width-1) solid var(--border-color-table);
    text-align: left;
    vertical-align: top;
  }

  & :deep(td) {
    background-color: var(--background-subtle);
  }
}

/* Keep in sync with .ai-block-aside in AiBlock/AiBlockAside.vue */
.ai-markdown--dimmed {
  --ai-markdown-title-color: var(--font-color-dimmed);

  font-size: var(--font-size-small);
  line-height: var(--dimension-6);
  color: var(--font-color-dimmed);
}
/* stylelint-enable selector-pseudo-class-no-unknown */
</style>
